import axios from 'axios'
import HTTP from './http'

const filenameFromDisposition = disposition => {
    if (!disposition) return ''
    const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition)
    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition)
    const encoded = (utf8 && utf8[1]) || (plain && plain[1])
    if (!encoded) return ''

    const filename = encoded.trim().replace(/^["']|["']$/g, '')
    try {
        return decodeURIComponent(filename)
    } catch (_) {
        return filename
    }
}

const downloadBlobResponse = (response, fallbackFilename) => {
    const disposition =
        response.headers && (response.headers['content-disposition'] || response.headers['Content-Disposition'])
    const filename = filenameFromDisposition(disposition) || fallbackFilename
    const url = window.URL.createObjectURL(response.data)
    const anchor = document.createElement('a')

    try {
        anchor.href = url
        anchor.download = filename
        anchor.style.display = 'none'
        document.body.appendChild(anchor)
        anchor.click()
    } finally {
        if (anchor.parentNode) anchor.parentNode.removeChild(anchor)
        window.URL.revokeObjectURL(url)
    }
}

const readBlobAsText = blob =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(reader.error)
        reader.readAsText(blob)
    })

const responseErrorPayload = async error => {
    const data = error && error.response && error.response.data
    if (!data) return {}
    if (typeof data === 'string') {
        try {
            return JSON.parse(data)
        } catch (_) {
            return {}
        }
    }
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            return JSON.parse(await readBlobAsText(data))
        } catch (_) {
            return {}
        }
    }
    return data
}

const downloadTransactionPdf = async ({ txnfdid, terminal }) => {
    const response = await axios.get(HTTP.TRANZILA_TRANSACTION_PDF, {
        params: { txnfdid, terminal },
        responseType: 'blob'
    })
    downloadBlobResponse(response, `tranzila-${txnfdid}.pdf`)
}

const TRANSACTION_EXPORT_FIELDS = [
    'index',
    'tranmode',
    'txn_type',
    'billing_kind',
    'group',
    'child_terminal',
    'user_defined_2',
    'user_defined_3',
    'user_defined_5',
    'user_defined_7',
    'transaction_date',
    'transaction_time',
    'processor_response_code',
    'amount',
    'txnfdid'
]

const transactionExportRow = row => {
    const trimmed: any = {}
    for (const field of TRANSACTION_EXPORT_FIELDS) {
        if (Object.prototype.hasOwnProperty.call(row, field)) trimmed[field] = row[field]
    }
    return trimmed
}

const downloadTransactionsFile = async ({ rows, meta }) => {
    const exportRows = rows.map(transactionExportRow)
    const response = await axios.post(
        HTTP.TRANZILA_TRANSACTIONS_FILE,
        { rows: exportRows, meta },
        { responseType: 'blob' }
    )
    downloadBlobResponse(response, `tranzila-transactions-${meta.from}_${meta.to}.xlsx`)
}

// The Documents page as a file. Unlike downloadTransactionsFile above, NOTHING is posted but the
// filters: the server re-runs the page's own query without pagination, so the file is the whole
// matching set and not the twenty rows the browser happens to be holding.
const DOCUMENTS_FALLBACK_FILENAME = 'tranzila-documents.xlsx'

const downloadDocumentsFile = async (filters, fallbackFilename?: string) => {
    const payload = filters || {}
    const response = await axios.post(HTTP.TRANZILA_DOCUMENTS_FILE, payload, { responseType: 'blob' })
    // The caller names the file from the filters it actually sent; this default only exists so
    // no code path can ever interpolate an undefined date into a filename.
    downloadBlobResponse(response, fallbackFilename || DOCUMENTS_FALLBACK_FILENAME)
}

export default {
    getStoTransactionData: payload => axios.get(HTTP.GET_STO_TRANZILA, { params: { ...payload } }),
    getStoHistoryData: payload => axios.get(HTTP.GET_STO_HISTORY, { params: { ...payload } }),
    getALLStoHistoryData: payload => axios.get(HTTP.GET_ALL_STO_HISTORY, { params: { ...payload } }),
    getStosList: payload => axios.get(HTTP.GET_STOS_LIST, { params: { ...payload } }),
    getStoListFile: payload => axios.post(HTTP.STO_LIST_FILE, payload, { responseType: 'blob' }),
    findUserDataForSto: payload => axios.get(HTTP.FIND_USER_DATA_FOR_STO, { params: { ...payload } }),
    updateStoStatus: payload => axios.patch(HTTP.UPD_STO_STATUS, { params: { ...payload } }),
    updateSubscriptionIdSto: payload => axios.patch(HTTP.UPD_SUB_ID_STO, { params: { ...payload } }),
    editSubStoData: payload => axios.patch(HTTP.EDIT_SUB_STO_DATA, { params: { ...payload } }),
    stoVerify: payload => axios.post(HTTP.STO_VERIFY, payload),
    stoCreate: payload => axios.post(HTTP.STO_CREATE, payload),
    stoDebit: payload => axios.post(HTTP.STO_DEBIT, payload),
    stoBankCharge: payload => axios.post(HTTP.STO_BANK_CHARGE, payload),
    stoBankDocument: payload => axios.post(HTTP.STO_BANK_DOCUMENT, payload),
    canceledSubTranzila: payload => axios.post(HTTP.CANCELED_SUB_TRANZILA, payload),
    updateStoCreatedAt: ({ user_id, user_subscription_id, sto_id, sto_created_at }) =>
        axios.post(HTTP.UPDATE_STO_CREATED_AT, { user_id, user_subscription_id, sto_id, sto_created_at }),
    // Admin Documents page. Reads the local cache; params are flat scalars, plus the sku list
    // which axios serialises as sku[]=A&sku[]=B.
    getDocuments: payload => axios.get(HTTP.TRANZILA_DOCUMENTS, { params: { ...payload } }),
    // Dropdown source for the SKU filter. Loaded once per page mount; the filter works without it.
    getDocumentsSkuOptions: () => axios.get(HTTP.TRANZILA_DOCUMENTS_SKU_OPTIONS),
    // Dropdown source for the מוצר filter: id, name, active. Loaded once per page mount.
    getDocumentsProductOptions: () => axios.get(HTTP.TRANZILA_DOCUMENTS_PRODUCT_OPTIONS),
    // Read-only repair queue behind the Documents page's second tab. Requested only when that
    // tab is opened; flat scalar params, same shape as getDocuments.
    getDocumentsUnresolved: payload => axios.get(HTTP.TRANZILA_DOCUMENTS_UNRESOLVED, { params: { ...payload } }),
    // Accept the automatic user match of one row. `expectedUserId` is the candidate the operator
    // confirmed -- a stale-confirmation precondition only: the server recomputes the candidate and
    // answers 409 if it is no longer that user.
    resolveDocumentUserAuto: (id, expectedUserId) =>
        axios.post(`${HTTP.TRANZILA_DOCUMENTS_UNRESOLVED}/${encodeURIComponent(String(id))}/resolve-user-auto`, {
            expected_user_id: String(expectedUserId)
        }),
    // Auto SKU V1: accept the code the row's own product string determines. The row id is the
    // whole request -- no sku is sent; the server recomputes it and may answer 409.
    resolveDocumentSkuAuto: id =>
        axios.post(`${HTTP.TRANZILA_DOCUMENTS_UNRESOLVED}/${encodeURIComponent(String(id))}/resolve-sku-auto`),
    // Manual V1, on modal open: the row's CURRENT instrument candidates. Read-only.
    getDocumentUserCandidates: id =>
        axios.get(`${HTTP.TRANZILA_DOCUMENTS_UNRESOLVED}/${encodeURIComponent(String(id))}/user-candidates`),
    // Manual V1: assign ONE of those candidates. The server accepts it only if it is still one.
    resolveDocumentUserManual: (id, userId) =>
        axios.post(`${HTTP.TRANZILA_DOCUMENTS_UNRESOLVED}/${encodeURIComponent(String(id))}/resolve-user-manual`, {
            user_id: String(userId)
        }),
    // Manual SKU V1, on modal open: advisory history + the server's full code list. Read-only.
    getDocumentSkuHistory: id =>
        axios.get(`${HTTP.TRANZILA_DOCUMENTS_UNRESOLVED}/${encodeURIComponent(String(id))}/sku-history`),
    // Manual SKU V1: the code the operator explicitly selected AND confirmed. The body is exactly
    // { sku } -- the reviewer, provenance and everything else are decided server-side.
    resolveDocumentSkuManual: (id, sku) =>
        axios.post(`${HTTP.TRANZILA_DOCUMENTS_UNRESOLVED}/${encodeURIComponent(String(id))}/resolve-sku-manual`, {
            sku: String(sku)
        }),
    // ONE cache row for the page's transaction-details modal, by the row id both tabs carry.
    // Requested per click only; the id is the API's decimal string and is never Number()'d.
    getDocumentTransactionDetails: id =>
        axios.get(`${HTTP.TRANZILA_DOCUMENTS}/${encodeURIComponent(String(id))}/transaction`),
    getTranzilaData: payload => axios.get(HTTP.TRANZILA_TRANSACTIONS, { params: { ...payload } }),
    // Lazy per-transaction line items for the details modal — one transaction, never the page.
    getTransactionItems: ({ terminal, transaction_id }) =>
        axios.get(HTTP.TRANZILA_TRANSACTION_ITEMS, { params: { terminal, transaction_id } }),
    searchTranzilaTransactions: payload => axios.get(HTTP.TRANZILA_TRANSACTIONS_SEARCH, { params: { ...payload } }),
    downloadTransactionPdf,
    downloadTransactionsFile,
    downloadDocumentsFile,
    responseErrorPayload
}
